import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@util/tailwind";

export const TooltipProvider = TooltipPrimitive.Provider;

export const TooltipRoot = TooltipPrimitive.Root;

export const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * PORTALLED, always. Radix positions a tooltip with `position: fixed` and
 * coordinates measured against the VIEWPORT, and its `Content` does not portal
 * itself — the caller has to add `Tooltip.Portal`.
 *
 * Rendered in place, those viewport coordinates are resolved against the
 * nearest ancestor that is a containing block for fixed descendants. An
 * element with `container-type` is one of those, and the app has six of them
 * (`[container-type:inline-size]` on the document rows, the list rows, the
 * merchant's document rows). A tooltip inside one landed at the top of the
 * page instead of beside what it describes.
 *
 * The portal moves the content to `document.body`, out of every container and
 * every `overflow: hidden`, which is where a floating element belongs.
 */
export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, forceMount, ...props }, ref) => (
  // `forceMount` goes to BOTH: the portal decides whether the subtree exists
  // at all, so a caller keeping a closed tooltip mounted (an animation, a
  // visual test) would otherwise have the portal unmount it anyway (Jack,
  // #4000).
  <TooltipPrimitive.Portal forceMount={forceMount}>
    <TooltipPrimitive.Content
      ref={ref}
      forceMount={forceMount}
      sideOffset={sideOffset}
      className={cn(
        "z-50 bg-black text-white px-3 py-1.5 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 opacity-90 rounded-md",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delayDuration?: number;
  asChild?: boolean;
}

export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  delayDuration = 300,
  asChild = false,
}: TooltipProps) {
  const [open, setOpen] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);

  React.useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleClick = React.useCallback(() => {
    if (isTouchDevice) {
      setOpen(!open);
    }
  }, [isTouchDevice, open]);

  // When asChild is true, we need to wrap children in a span to avoid 
  // data-state conflicts with other Radix components like TabsTrigger
  const triggerContent = asChild ? children : <span>{children}</span>;

  return (
    <TooltipProvider>
      <TooltipRoot
        delayDuration={delayDuration}
        open={isTouchDevice ? open : undefined}
        onOpenChange={isTouchDevice ? setOpen : undefined}
      >
        <TooltipTrigger asChild onClick={handleClick}>
          {triggerContent}
        </TooltipTrigger>
        {content && (
          <TooltipContent side={side} align={align}>
            {content}
          </TooltipContent>
        )}
      </TooltipRoot>
    </TooltipProvider>
  );
}
